// ** React Imports
import React, { useEffect, useMemo, useState } from 'react'

// ** MUI Imports
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'

// ** Third Party Imports
import { useDebouncedCallback } from 'use-debounce'

// ** API / Helpers
import { getWorkspaceFilters, searchWorkspacePrices } from '../api'
import { formatMoney, periodLabel, PLAN_COLORS, StatusChip, SummaryChips } from './helpers'

const PAGE_SIZE = 10

// ** One table row per plan variant (Flexible, Annual monthly, Annual yearly, ...), one column per currency
const pivotOffers = (offers, currencies) => {
  const rows = new Map()
  offers.forEach(o => {
    const key = [o.offerDisplayName, o.plan, o.cycle?.periodType, o.cycle?.duration].join('|')
    if (!rows.has(key)) rows.set(key, { key, label: o.offerDisplayName || o.plan, plan: o.plan, cycle: o.cycle, cells: {} })
    const row = rows.get(key)
    if (!row.cells[o.currency]) row.cells[o.currency] = []
    row.cells[o.currency].push(o)
  })
  const columns = currencies.filter(c => offers.some(o => o.currency === c))
  return { rows: Array.from(rows.values()), columns }
}

const offerTooltip = o => {
  const lines = []
  if (o.unitsRanges?.length)
    lines.push(
      'Seats: ' +
        Array.from(new Set(o.unitsRanges.map(r => `${r.min ?? 1}–${r.max ?? '∞'}`))).join(', ')
    )
  if (o.regions?.length) lines.push(`Regions (${o.regions.length}): ${o.regions.slice(0, 25).join(', ')}${o.regions.length > 25 ? '…' : ''}`)
  if (o.startTime) lines.push(`From ${o.startTime.slice(0, 10)}${o.endTime ? ` to ${o.endTime.slice(0, 10)}` : ''}`)
  if (o.variants > 1) lines.push(`${o.variants} Google offers with this price`)
  if (o.resourceType) lines.push(`Per ${o.resourceType.toLowerCase()}`)
  return lines.join('\n')
}

const PriceCell = ({ offers, currency, compare }) => (
  <>
    {offers.map((o, i) => {
      const { base, effective, discount } = o.price || {}
      const monthly = o.cycleMonths && o.cycleMonths !== 1 && base !== null ? base / o.cycleMonths : null
      return (
        <Box key={i} sx={{ mb: i < offers.length - 1 ? 1.5 : 0 }}>
          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{offerTooltip(o)}</span>}>
            <Typography variant='body2' sx={{ fontWeight: 600, cursor: 'help' }}>
              {formatMoney(base, currency)}
              <Typography component='span' variant='caption' color='text.secondary'>
                {' '}
                {periodLabel(o.cycle)}
              </Typography>
              {o.upcoming && <Chip size='small' label='Upcoming' sx={{ ml: 1, height: 18, fontSize: 10 }} />}
            </Typography>
          </Tooltip>
          {monthly !== null && (
            <Typography variant='caption' color='text.secondary' display='block'>
              = {formatMoney(monthly, currency)} /mo
            </Typography>
          )}
          {effective !== null && effective !== undefined && effective !== base && (
            <Typography variant='caption' color='text.secondary' display='block'>
              Cost {formatMoney(effective, currency)}
              {discount ? ` (-${discount}%)` : ''}
            </Typography>
          )}
          {compare && o.app && !['n/a', 'not_in_app'].includes(o.app.status) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant='caption'>
                App: {o.app.price !== null && o.app.price !== undefined ? formatMoney(o.app.price, currency) : '—'}
              </Typography>
              <StatusChip app={o.app} currency={currency} />
            </Box>
          )}
        </Box>
      )
    })}
  </>
)

const SkuCard = ({ group, currencies, compare }) => {
  const { rows, columns } = useMemo(() => pivotOffers(group.offers, currencies), [group, currencies])

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant='h6' sx={{ mr: 1 }}>
            {group.skuDisplayName || group.skuId}
          </Typography>
          <Chip size='small' label={group.productName} />
          <Typography variant='caption' color='text.secondary'>
            {group.skuId}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {compare &&
            (group.appProduct ? (
              <Tooltip
                title={`App product #${group.appProduct.id} (skuId ${group.appProduct.skuId}), matched by ${
                  group.appProduct.matchedBy === 'name' ? 'SKU name' : 'SKU id'
                }`}
              >
                <Chip size='small' color='success' variant='outlined' label={`In app: ${group.appProduct.skuName}`} />
              </Tooltip>
            ) : (
              <Chip size='small' variant='outlined' label='Not in app' />
            ))}
        </Box>
        {group.skuDescription && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {group.skuDescription}
          </Typography>
        )}
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                {columns.map(c => (
                  <TableCell key={c}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.key} hover>
                  <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>
                    <Typography variant='body2'>{row.label}</Typography>
                    <Chip
                      size='small'
                      color={PLAN_COLORS[row.plan] || 'default'}
                      label={row.plan}
                      sx={{ height: 18, fontSize: 10, mt: 0.5 }}
                    />
                  </TableCell>
                  {columns.map(c => (
                    <TableCell key={c} sx={{ verticalAlign: 'top' }}>
                      {row.cells[c] ? <PriceCell offers={row.cells[c]} currency={c} compare={compare} /> : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

const WorkspacePrices = ({ refreshKey, onFetched }) => {
  const [filters, setFilters] = useState(null)
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const [currencies, setCurrencies] = useState([])
  const [plan, setPlan] = useState('')
  const [region, setRegion] = useState(null)
  const [compare, setCompare] = useState(true)
  const [issuesOnly, setIssuesOnly] = useState(false)
  const [inAppOnly, setInAppOnly] = useState(true)
  const [page, setPage] = useState(1)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const debounced = useDebouncedCallback(value => {
    setSearch(value)
    setPage(1)
  }, 400)

  useEffect(() => {
    getWorkspaceFilters(refreshKey > 0)
      .then(data => {
        setFilters(data)
        // ** Default to the currencies the app bills in
        setCurrencies(prev =>
          prev.length ? prev : data.currencies.filter(c => (data.appCurrencies || []).includes(c))
        )
      })
      .catch(err => setError(err.message))
  }, [refreshKey])

  useEffect(() => {
    if (!filters) return
    setLoading(true)
    setError(null)
    searchWorkspacePrices({
      q: search,
      currency: currencies,
      plan: plan || undefined,
      region: region || undefined,
      compare: compare || undefined,
      issuesOnly: compare && issuesOnly ? true : undefined,
      inAppOnly: inAppOnly || undefined,
      page,
      limit: PAGE_SIZE
    })
      .then(data => {
        setResult(data)
        onFetched && onFetched(data.fetchedAt)
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [filters, search, currencies, plan, region, compare, issuesOnly, inAppOnly, page])

  const resetPage = setter => value => {
    setter(value)
    setPage(1)
  }

  const shownCurrencies = currencies.length ? currencies : filters?.currencies || []

  return (
    <Box>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems='center'>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size='small'
                label='Search a product'
                placeholder='e.g. Business Standard, Gemini Enterprise, Voice'
                value={q}
                onChange={e => {
                  setQ(e.target.value)
                  debounced(e.target.value)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <ToggleButtonGroup
                size='small'
                value={currencies}
                onChange={(e, value) => resetPage(setCurrencies)(value)}
                sx={{ flexWrap: 'wrap' }}
              >
                {(filters?.currencies || []).map(c => (
                  <ToggleButton key={c} value={c} sx={{ px: 2 }}>
                    {c}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size='small'>
                <InputLabel>Plan</InputLabel>
                <Select label='Plan' value={plan} onChange={e => resetPage(setPlan)(e.target.value)}>
                  <MenuItem value=''>All plans</MenuItem>
                  {(filters?.plans || []).map(p => (
                    <MenuItem key={p} value={p}>
                      {p.charAt(0) + p.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Autocomplete
                size='small'
                options={filters?.regions || []}
                value={region}
                onChange={(e, value) => resetPage(setRegion)(value)}
                renderInput={params => <TextField {...params} label='Customer region' />}
              />
            </Grid>
            <Grid item xs={12} md='auto'>
              <FormControlLabel
                control={<Switch checked={compare} onChange={e => resetPage(setCompare)(e.target.checked)} />}
                label='Compare with app prices'
              />
            </Grid>
            <Grid item xs={12} md='auto'>
              <FormControlLabel
                control={<Switch checked={inAppOnly} onChange={e => resetPage(setInAppOnly)(e.target.checked)} />}
                label='In app only'
              />
            </Grid>
            {compare && (
              <>
                <Grid item xs={12} sm={4} md='auto'>
                  <FormControlLabel
                    control={<Switch checked={issuesOnly} onChange={e => resetPage(setIssuesOnly)(e.target.checked)} />}
                    label='Issues only'
                  />
                </Grid>
              </>
            )}
          </Grid>
          {compare && result?.totals && (
            <Box sx={{ mt: 3 }}>
              <SummaryChips totals={result.totals} />
              <Typography variant='caption' color='text.secondary' display='block'>
                Flexible and commitment plans are compared per seat per month (yearly prices divided by 12). Counts
                cover all matching products, not only this page.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {error && (
        <Alert severity='error' sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {loading && !result ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : result ? (
        <Box sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity .2s' }}>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {result.total} product{result.total === 1 ? '' : 's'}
          </Typography>
          {result.data.length === 0 && <Alert severity='info'>No product matches these filters.</Alert>}
          {result.data.map(group => (
            <SkuCard key={group.skuId} group={group} currencies={shownCurrencies} compare={compare} />
          ))}
          {result.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={result.totalPages} page={page} onChange={(e, value) => setPage(value)} />
            </Box>
          )}
        </Box>
      ) : null}
    </Box>
  )
}

export default WorkspacePrices
